import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import usePrevious from 'react-utils/hooks/usePrevious';
import { AuthContext } from '../contexts/AuthContext';
import { EditorConfigContext } from '../contexts/EditorConfigContext';
import useDebouncedEditorStateContext from '../contexts/useDebouncedEditorStateContext';
import { PERSONALIZATION_TOKEN } from '../editorCapabilities/constants';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Badge } from 'foundations-components/transitional/components/Badge';
import useContentSearch from '../utils/useContentSearch';
import { buildPersonalizationTokenTr } from '../utils/documentUtils';
import { PERSONALIZATION_TOKENS_P2 } from '../utils/personalizationTokenUtils';

/**
 * Custom styles to apply to personalization token blocks in the editor.
 * These override the default token appearance. See fallbacks in PersonalizationTokenView.
 */

/**
 * Represents a single personalization token that can be inserted into the editor.
 * Tokens are placeholders that get replaced with actual values when the content is sent.
 */

import { View } from 'foundations-components/components/View';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { PopoverBody } from 'foundations-components/transitional/components/Popover';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { Button } from 'foundations-components/transitional/components/Button';
import { Select } from 'foundations-components/transitional/components/Select';
import { Flex } from 'foundations-components/components/Flex';
import { Heading } from 'foundations-components/transitional/components/Heading';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const noop = () => {};

/** Configuration for the Personalization Token capability (merge fields) */

const DefaultTokenValueInput = ({
  value,
  onChange
}) => {
  return /*#__PURE__*/_jsx(FormControl, {
    label: I18n.text('hubspot-prosemirror.personalizationToken.popover.defaultValue'),
    marginBlockStart: "100",
    children: /*#__PURE__*/_jsx(TextInput, {
      autoFocus: true,
      onChange: onChange,
      value: value,
      placeholder: I18n.text('hubspot-prosemirror.personalizationToken.popover.defaultValuePlaceholder')
    })
  });
};
const FallbackValueInput = ({
  value,
  onChange,
  onKeyDown
}) => {
  return /*#__PURE__*/_jsx(FormControl, {
    label: I18n.text('hubspot-prosemirror.personalizationToken.inlineDropdown.fallbackLabel'),
    marginBlockStart: "100",
    children: /*#__PURE__*/_jsx(TextInput, {
      autoFocus: true,
      onChange: onChange,
      onKeyDown: onKeyDown,
      value: value,
      placeholder: I18n.text('hubspot-prosemirror.personalizationToken.inlineDropdown.fallbackPlaceholder')
    })
  });
};
const tokenPrefixToMessageKeyMap = {
  company: 'hubspot-prosemirror.personalizationToken.tokenGroups.company',
  contact: 'hubspot-prosemirror.personalizationToken.tokenGroups.contact',
  deal: 'hubspot-prosemirror.personalizationToken.tokenGroups.deal',
  host: 'hubspot-prosemirror.personalizationToken.tokenGroups.host',
  meeting: 'hubspot-prosemirror.personalizationToken.tokenGroups.meeting',
  quote: 'hubspot-prosemirror.personalizationToken.tokenGroups.quote',
  sender: 'hubspot-prosemirror.personalizationToken.tokenGroups.sender',
  ticket: 'hubspot-prosemirror.personalizationToken.tokenGroups.ticket'
};
function getLabelForTokenPrefix(tokenPrefix) {
  return I18n.text(tokenPrefixToMessageKeyMap[tokenPrefix]);
}
function isOptionsWithTokenStylesGrouped(options) {
  if (!options.length) throw new Error('Options array is empty');
  return options[0].options !== undefined;
}
export function transformOptions(data, groupTokens) {
  if (groupTokens) {
    if (!data) return null;
    const groupedOptions = data.reduce((acc, token) => {
      const tokenPrefix = token.groupName || token.tokenKey.split('.')[0];
      if (!acc[tokenPrefix]) {
        acc[tokenPrefix] = {
          options: [],
          text: token.groupName || getLabelForTokenPrefix(tokenPrefix),
          value: tokenPrefix
        };
      }
      acc[tokenPrefix].options.push({
        text: token.displayText,
        tokenStyles: token.tokenStyles,
        value: token.tokenKey
      });
      return acc;
    }, {});
    return Object.values(groupedOptions);
  }
  return data && data.map(token => ({
    text: token.displayText,
    value: token.tokenKey,
    tokenStyles: token.tokenStyles
  }));
}
export function PersonalizationTokenPopover({
  initialTokenKey,
  initialDefaultValue = '',
  isUngatedForP2 = false,
  nodePos,
  onPopoverConfirm
}) {
  const shouldInsertNewToken = nodePos === undefined;
  const {
    [PERSONALIZATION_TOKEN]: personalizationTokenConfig
  } = useContext(EditorConfigContext);
  const {
    fetchTokens,
    groupTokens,
    allowDefaultValue,
    defaultValue
  } = personalizationTokenConfig;
  const isDefaultValueRequired = defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.required;
  const {
    editorState,
    setEditorState
  } = useDebouncedEditorStateContext();
  const [defaultTokenValue, setDefaultTokenValue] = useState(initialDefaultValue);
  const [selectedToken, setSelectedToken] = useState(initialTokenKey || null);
  const hasTokenSelected = selectedToken !== null && selectedToken !== undefined;
  const showDefaultValueInput = isUngatedForP2 ? hasTokenSelected && (Boolean(defaultValue) || allowDefaultValue) : Boolean(defaultValue) || allowDefaultValue;
  const transformFunction = useMemo(() => data => transformOptions(data, groupTokens), [groupTokens]);
  const [__data, searchQuery, setSearchQuery, options, isLoading] = useContentSearch(fetchTokens, transformFunction, noop);
  const _inputRef = useRef(null);
  const prevOption = usePrevious(options);
  const wasLoading = usePrevious(isLoading);
  useEffect(() => {
    const didPreviouslyRenderTypeahead = prevOption && prevOption.length > 0;
    const isTypeaheadRendered = options && options.length > 0;
    if ((!didPreviouslyRenderTypeahead && isTypeaheadRendered || wasLoading && !isLoading) && _inputRef && _inputRef.current) {
      _inputRef.current.focus();
    }
  }, [options, _inputRef, isLoading, wasLoading, prevOption]);
  const handleSearch = useCallback(value => setSearchQuery(value), [setSearchQuery]);
  const handleChange = useCallback(e => {
    setSelectedToken(e.target.value);
  }, [setSelectedToken]);
  const handleOnInsert = useCallback(() => {
    if (!selectedToken) {
      return;
    }
    const selectedOption = function () {
      if (!options) return undefined;
      let flatOptions = options;
      if (isOptionsWithTokenStylesGrouped(options)) {
        flatOptions = options.flatMap(group => group.options);
      }
      return flatOptions.find(option => option.value === selectedToken);
    }();
    const tr = buildPersonalizationTokenTr({
      tokenKey: selectedToken,
      displayText: selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.text,
      tokenStyles: selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.tokenStyles,
      defaultTokenValue,
      editorState,
      nodePos
    });
    setEditorState(editorState.apply(tr));
    onPopoverConfirm();
  }, [editorState, setEditorState, defaultTokenValue, selectedToken, options, nodePos, onPopoverConfirm]);

  // This prevents the typeahead from closing when the user presses the spacebar.
  // Typically, this keydown bubbles up to the Dropdown component, and causes
  // the dropdown to close. For any keyboard events that are targeted at the input
  // we want to prevent that from happening.
  // The escape key can still be used to close the dropdown.
  const keydownInterceptor = useCallback(e => {
    if (e.target instanceof HTMLInputElement && e.key !== 'Escape') {
      e.stopPropagation();
    }
  }, []);
  const disableInsertToken = selectedToken === null || selectedToken === undefined || !isUngatedForP2 && isDefaultValueRequired && !defaultTokenValue;
  const handleFallbackKeyDown = useCallback(e => {
    if (e.key === 'Enter' && !disableInsertToken) {
      e.preventDefault();
      e.stopPropagation();
      handleOnInsert();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onPopoverConfirm();
    }
  }, [disableInsertToken, handleOnInsert, onPopoverConfirm]);
  if (isLoading && searchQuery.length === 0) {
    return /*#__PURE__*/_jsx(Flex, {
      minBlockSize: "100px",
      justifySelf: "center",
      children: /*#__PURE__*/_jsx(LoadingSpinner, {})
    });
  }
  if (!options) {
    return /*#__PURE__*/_jsx(View, {
      margin: "200",
      as: "div",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "hubspot-prosemirror.personalizationToken.popover.error"
      })
    });
  }
  const selectLabel = isUngatedForP2 ? I18n.text('hubspot-prosemirror.personalizationToken.popover.userTokenLabel') : shouldInsertNewToken ? I18n.text('hubspot-prosemirror.personalizationToken.popover.addContactToken') : I18n.text('hubspot-prosemirror.personalizationToken.popover.updateContactToken');
  return /*#__PURE__*/_jsxs(PopoverBody, {
    children: [/*#__PURE__*/_jsx(Heading, {
      tagName: "h2",
      typography: "heading-100",
      marginBlockEnd: "300",
      children: I18n.text(shouldInsertNewToken ? 'hubspot-prosemirror.personalizationToken.popover.header' : 'hubspot-prosemirror.personalizationToken.popover.editHeader')
    }), /*#__PURE__*/_jsxs("div", {
      onKeyDown: keydownInterceptor,
      children: [/*#__PURE__*/_jsx(FormControl, {
        label: selectLabel,
        children: /*#__PURE__*/_jsx(Select, {
          "data-test-id": "personalization-token-select",
          options: options,
          placeholder: I18n.text('hubspot-prosemirror.personalizationToken.popover.searchPlaceholder'),
          onInputChange: handleSearch,
          onChange: handleChange,
          inputRef: _inputRef,
          value: selectedToken
        })
      }), showDefaultValueInput && (isUngatedForP2 ? /*#__PURE__*/_jsx(FallbackValueInput, {
        value: defaultTokenValue,
        onChange: e => setDefaultTokenValue(e.target.value),
        onKeyDown: handleFallbackKeyDown
      }) : /*#__PURE__*/_jsx(DefaultTokenValueInput, {
        value: defaultTokenValue,
        onChange: e => setDefaultTokenValue(e.target.value)
      }))]
    }), /*#__PURE__*/_jsx(View, {
      marginBlock: "500",
      children: /*#__PURE__*/_jsxs(Flex, {
        gap: "200",
        children: [/*#__PURE__*/_jsx(Button, {
          onClick: handleOnInsert,
          use: "primary",
          size: "sm",
          disabled: disableInsertToken,
          children: I18n.text(shouldInsertNewToken ? 'hubspot-prosemirror.personalizationToken.popover.insertButton' : isUngatedForP2 ? 'hubspot-prosemirror.personalizationToken.popover.saveButton' : 'hubspot-prosemirror.personalizationToken.popover.updateButton')
        }), isUngatedForP2 && /*#__PURE__*/_jsx(Button, {
          onClick: onPopoverConfirm,
          use: "tertiary-light",
          size: "sm",
          children: I18n.text('hubspot-prosemirror.personalizationToken.inlineDropdown.cancelButton')
        })]
      })
    })]
  });
}
PersonalizationTokenPopover.displayName = 'PersonalizationTokenPopover';
function InsertPersonalizationToken({
  buttonText
}) {
  const [open, setOpen] = useState(false);
  const auth = useContext(AuthContext);
  const isUngatedForP2 = Boolean(auth && auth.gates.includes(PERSONALIZATION_TOKENS_P2));
  const onPopoverConfirm = useCallback(() => {
    setOpen(false);
  }, []);
  const onOpenChange = e => setOpen(e.target.value);
  const ContentWrapper = useCallback(() => /*#__PURE__*/_jsx(PersonalizationTokenPopover, {
    onPopoverConfirm: onPopoverConfirm,
    isUngatedForP2: isUngatedForP2
  }), [onPopoverConfirm, isUngatedForP2]);
  const dropdown = /*#__PURE__*/_jsx(Dropdown, {
    "data-test-id": "personalization-token",
    buttonUse: "transparent",
    menuWidth: 400,
    padding: "0",
    open: open,
    onOpenChange: onOpenChange,
    buttonText: buttonText || /*#__PURE__*/_jsx(FormattedMessage, {
      message: "hubspot-prosemirror.personalizationToken.toolbarButton"
    }),
    Content: ContentWrapper
  });
  if (buttonText || !isUngatedForP2) {
    return dropdown;
  }
  return /*#__PURE__*/_jsx(Tooltip, {
    open: open ? false : undefined,
    title: /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "row",
      gap: "100",
      alignItems: "center",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "hubspot-prosemirror.tooltips.insertPersonalizationToken"
      }), /*#__PURE__*/_jsx(Badge, {
        use: "koala",
        children: '{{'
      })]
    }),
    children: dropdown
  });
}
InsertPersonalizationToken.displayName = 'InsertPersonalizationToken';
export default InsertPersonalizationToken;